import React, { useState, useEffect } from 'react';
import { useFormik } from 'formik';
import * as yup from 'yup';
import { useNavigate, useLocation } from 'react-router-dom';
import { BsEye, BsEyeSlash } from 'react-icons/bs'; // For showing/hiding password
import { postForgotPasswordset } from '../../api/create'; // Replace with your API call function
import { toast } from 'react-toastify';

const ForgotPasswordSet: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [showPassword, setShowPassword] = useState(false);
  const [userEmail, setUserEmail] = useState<string | null>(null);
  const [temToken, setTemToken] = useState<string | null>(null);
  const [urlStatus, setUrlStatus] = useState<boolean | null>(null);
  const [linkError, setLinkError] = useState<string | null>(null);
  const [passwordRules, setPasswordRules] = useState({
    minLength: false,
    hasNumber: false,
    hasCharacter: false,
    passwordsMatch: false,
  });

  useEffect(() => {
    // Parse the URL parameters for `user` and `tem_token`
    const queryParams = new URLSearchParams(location.search);
    const fetchedUser = queryParams.get('user');
    const fetchedTemToken = queryParams.get('token');
    const status = queryParams.get('status');
    const linkError = queryParams.get('message'); 

    // Update state with the parsed values
    setUserEmail(fetchedUser);
    setTemToken(fetchedTemToken);
    setLinkError(linkError);
    if (status === "true") {
      setUrlStatus(true);
    } else {
      const timer = setTimeout(() => {
        navigate('/login'); // Navigate to the login page
      }, 2000);

      // Cleanup the timer on component unmount
      return () => clearTimeout(timer);
    }
  }, [location.search, navigate]);

  const validatePassword = (password: string, confirmPassword: string) => {
    setPasswordRules({
      minLength: password.length >= 8,
      hasNumber: /\d/.test(password),
      hasCharacter: /[A-Za-z]/.test(password),
      passwordsMatch: !!password && !!confirmPassword && password === confirmPassword, // Explicit boolean coercion
    });
  };

  const formik = useFormik({
    initialValues: {
      otp: '',
      password: '',
      confirmPassword: '',
    },
    validationSchema: yup.object({
      otp: yup.string().required('OTP is required'),
      password: yup
        .string()
        .min(8, '')
        .required('Password is required'),
      confirmPassword: yup
        .string()
        .oneOf([yup.ref('password')], '')
        .required('Confirm Password is required'),
    }),
    onSubmit: async (values) => {
      try {
        const response = await postForgotPasswordset({
          email_encode: userEmail,
          token: temToken,
          OTP: values.otp,
          new_password: values.password,
          confirm_password: values.confirmPassword,
        });

        if (response && response.success === true) {
          toast.success("Password Reset Successfully");
          setTimeout(() => {
            navigate('/login');
          }, 3000);
        } else {
          toast.error(response?.detail.message || 'Failed to reset password')
        }
      } catch (error) {
        console.error('Error:', error);
        toast.error('An error occurred. Please try again later.');
      }
    },
  });

  const handlePasswordChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    formik.handleChange(e);
    validatePassword(e.target.value, formik.values.confirmPassword);
  };

  const handleConfirmPasswordChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    formik.handleChange(e);
    validatePassword(formik.values.password, e.target.value);
  };

  if (!userEmail || !temToken || !urlStatus) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gradient-to-r from-[#215564] to-[#163942]">
        <div className="w-full max-w-md p-8 bg-white rounded-lg shadow-md">
          <p className="text-center text-2xl text-red-600">{linkError}</p>
          <p className="text-center text-xl text-gray-600">Redirecting to the Login page</p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex items-center justify-center min-h-screen bg-gradient-to-r from-[#215564] to-[#163942]">
      <div className="w-full max-w-md p-8 bg-white rounded-lg shadow-md">
        <h2 className="text-2xl font-bold text-center text-gray-700">Reset Password</h2>
        <p className="text-center text-gray-500">
          Enter the OTP, new password, and confirm your password to reset your account.
        </p>

        <form onSubmit={formik.handleSubmit} className="space-y-6 mt-6">
          <div>
            <label htmlFor="otp" className="block text-sm font-medium text-gray-600">
              OTP
            </label>
            <input
              name="otp"
              type="text"
              placeholder="Enter OTP"
              value={formik.values.otp}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              className={`w-full px-4 py-2 mt-2 text-sm text-gray-700 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                formik.touched.otp && formik.errors.otp ? 'border-red-500' : 'border-gray-300'
              }`}
            />
            {formik.touched.otp && formik.errors.otp && (
              <div className="mt-1 text-sm text-red-500">{formik.errors.otp}</div>
            )}
          </div>

          <div>
            <label htmlFor="password" className="block text-sm font-medium text-gray-600">
              New Password
            </label>
            <div className='pl-4'>
                <ul className="mt-2 space-y-1 text-xs list-none">
                  <li className={passwordRules.hasNumber ? 'text-green-500' : 'text-red-500'}>
                    {passwordRules.hasNumber ? '✔' : '•'} Contains at least one number
                  </li>
                  <li className={passwordRules.hasCharacter ? 'text-green-500' : 'text-red-500'}>
                    {passwordRules.hasCharacter ? '✔' : '•'} Contains at least one letter
                  </li>
                  <li className={passwordRules.minLength ? 'text-green-500' : 'text-red-500'}>
                    {passwordRules.minLength ? '✔' : '•'} At least 8 characters
                  </li>
                  <li className={passwordRules.passwordsMatch ? 'text-green-500' : 'text-red-500'}>
                    {passwordRules.passwordsMatch ? '✔' : '•'} Passwords match
                  </li>
              </ul>
            </div>
            <div className="relative">
              <input
                name="password"
                type={showPassword ? 'text' : 'password'}
                placeholder="Enter new password"
                value={formik.values.password}
                onChange={handlePasswordChange}
                onBlur={formik.handleBlur}
                className={`w-full px-4 py-2 mt-2 text-sm text-gray-700 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                  formik.touched.password && formik.errors.password ? 'border-red-500' : 'border-gray-300'
                }`}
              />
              <span
                className="absolute right-3 top-5 text-gray-500 cursor-pointer"
                onClick={() => setShowPassword(!showPassword)}
              >
                {showPassword ? <BsEye /> : <BsEyeSlash />}
              </span>
            </div>
            {formik.touched.password && formik.errors.password && (
              <div className="mt-1 text-sm text-red-500">{formik.errors.password}</div>
            )}
          </div>

          <div>
            <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-600">
              Confirm Password
            </label>
            <input
              name="confirmPassword"
              type={showPassword ? 'text' : 'password'}
              placeholder="Confirm your password"
              value={formik.values.confirmPassword}
              onChange={handleConfirmPasswordChange}
              onBlur={formik.handleBlur}
              className={`w-full px-4 py-2 mt-2 text-sm text-gray-700 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                formik.touched.confirmPassword && formik.errors.confirmPassword ? 'border-red-500' : 'border-gray-300'
              }`}
            />
            {formik.touched.confirmPassword && formik.errors.confirmPassword && (
              <div className="mt-1 text-sm text-red-500">{formik.errors.confirmPassword}</div>
            )}
          </div>

          <button
            type="submit"
            disabled={formik.isSubmitting || !formik.isValid}
            className="w-full px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 focus:outline-none focus:ring-4 focus:ring-blue-300 disabled:opacity-50"
          >
            {formik.isSubmitting ? 'Resetting...' : 'Reset Password'}
          </button>
        </form>
      </div>
    </div>
  );
};

export default ForgotPasswordSet;
